/**
 * Notification Context
 * 
 * Provides centralized notification state management with Redis caching
 * 
 * Features:
 * - Redis-backed notification list caching (3min TTL)
 * - Redis-backed unread counter (atomic operations)
 * - Redis-backed preference caching (5min TTL)
 * - Real-time subscription with cache synchronization
 * - Automatic cache invalidation on mutations
 * - Type-safe notification access
 * 
 * Usage:
 * ```jsx
 * import { useNotifications } from '../contexts/NotificationContext';
 * 
 * function MyComponent() {
 *   const { 
 *     notifications, 
 *     unreadCount, 
 *     markAsRead,
 *     preferences,
 *     updatePreferences
 *   } = useNotifications();
 * }
 * ```
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import notificationService from '../utils/notificationService';
import supabase from '../utils/supabase';

const NotificationContext = createContext(null);

/**
 * Hook to access notification context
 * @returns {Object} Notification context value
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  
  if (!context) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('useNotifications: Context not available, returning fallback');
    }
    
    // Return fallback object for components outside provider
    return {
      notifications: [],
      unreadCount: 0,
      preferences: null,
      isLoading: false,
      error: null,
      fetchNotifications: async () => {},
      markAsRead: async () => ({ success: false, error: 'Context not available' }),
      markAllAsRead: async () => ({ success: false, error: 'Context not available' }),
      deleteNotification: async () => ({ success: false, error: 'Context not available' }),
      getUnreadCount: async () => 0,
      getPreferences: async () => null,
      updatePreferences: async () => ({ success: false, error: 'Context not available' }),
      getNotificationsByType: () => [],
      getUnreadNotifications: () => [],
      getHighPriorityNotifications: () => []
    };
  }
  
  return context;
};

/**
 * Notification Provider Component
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [realtimeSubscription, setRealtimeSubscription] = useState(null);

  /**
   * Fetch notifications from service (uses Redis cache)
   */
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await notificationService.getUserNotifications(user.id);

      if (result.success) {
        setNotifications(result.data || []);
        
        if (process.env.NODE_ENV === 'development' && result.fromCache) {
          console.log('📦 Notifications loaded from cache:', result.data?.length);
        }
      } else {
        setError(result.error || 'Failed to fetch notifications');
        console.error('❌ Failed to fetch notifications:', result.error);
      }
    } catch (err) {
      setError(err.message);
      console.error('❌ Exception fetching notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  /**
   * Fetch unread count from service (uses Redis cache)
   */
  const getUnreadCount = useCallback(async () => {
    if (!user?.id) return 0;

    try {
      const result = await notificationService.getUnreadCount(user.id);

      if (result.success) {
        setUnreadCount(result.count || 0);
        return result.count || 0;
      } else {
        console.error('❌ Failed to fetch unread count:', result.error);
        return 0;
      }
    } catch (err) {
      console.error('❌ Exception fetching unread count:', err);
      return 0;
    }
  }, [user?.id]);

  /**
   * Fetch user preferences from service (uses Redis cache)
   */
  const getPreferences = useCallback(async () => {
    if (!user?.id) return null;

    try {
      const result = await notificationService.getNotificationPreferences(user.id);

      if (result.success) {
        setPreferences(result.data);
        
        if (process.env.NODE_ENV === 'development' && result.fromCache) {
          console.log('📦 Notification preferences loaded from cache');
        }
        
        return result.data;
      } else {
        console.error('❌ Failed to fetch preferences:', result.error);
        return null;
      }
    } catch (err) {
      console.error('❌ Exception fetching preferences:', err);
      return null;
    }
  }, [user?.id]);

  /**
   * Update user notification preferences
   * @param {Object} newPreferences - New preference values
   */
  const updatePreferences = useCallback(async (newPreferences) => {
    if (!user?.id) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      const result = await notificationService.updateNotificationPreferences(
        user.id,
        newPreferences
      );

      if (result.success) {
        setPreferences(result.data);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Notification preferences updated (cache invalidated)');
        }
      }

      return result;
    } catch (err) {
      console.error('❌ Exception updating preferences:', err);
      return { success: false, error: err.message };
    }
  }, [user?.id]);

  /**
   * Mark a notification as read
   * @param {string} notificationId - Notification ID
   */
  const markAsRead = useCallback(async (notificationId) => {
    if (!user?.id || !notificationId) {
      return { success: false, error: 'Missing required parameters' };
    }

    try {
      const result = await notificationService.markAsRead(notificationId);

      if (result.success) {
        // Update local state optimistically
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));

        // Refresh from cache/DB to ensure consistency
        await fetchNotifications();
        await getUnreadCount();

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Notification marked as read (cache invalidated)');
        }
      }

      return result;
    } catch (err) {
      console.error('❌ Exception marking notification as read:', err);
      return { success: false, error: err.message };
    }
  }, [user?.id, fetchNotifications, getUnreadCount]);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    if (!user?.id) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      const result = await notificationService.markAllAsRead(user.id);

      if (result.success) {
        // Update local state optimistically
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);

        // Refresh from cache/DB to ensure consistency
        await fetchNotifications();

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ All notifications marked as read (cache invalidated)');
        }
      }

      return result;
    } catch (err) {
      console.error('❌ Exception marking all as read:', err);
      return { success: false, error: err.message };
    }
  }, [user?.id, fetchNotifications]);

  /**
   * Delete a notification
   * @param {string} notificationId - Notification ID
   */
  const deleteNotification = useCallback(async (notificationId) => {
    if (!user?.id || !notificationId) {
      return { success: false, error: 'Missing required parameters' };
    }

    try {
      const result = await notificationService.deleteNotification(notificationId);

      if (result.success) {
        // Update local state optimistically
        setNotifications(prev => prev.filter(n => n.id !== notificationId));

        // Refresh to ensure consistency
        await fetchNotifications();
        await getUnreadCount();

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Notification deleted (cache invalidated)');
        }
      }

      return result;
    } catch (err) {
      console.error('❌ Exception deleting notification:', err);
      return { success: false, error: err.message };
    }
  }, [user?.id, fetchNotifications, getUnreadCount]);

  /**
   * Setup real-time subscription for notifications
   */
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔔 New notification received (real-time):', payload.new);
          }

          // Refresh notifications and count from cache/DB
          await fetchNotifications();
          await getUnreadCount();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔄 Notification updated (real-time):', payload.new);
          }

          // Refresh notifications and count from cache/DB
          await fetchNotifications();
          await getUnreadCount();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('🗑️  Notification deleted (real-time):', payload.old);
          }

          // Refresh notifications and count from cache/DB
          await fetchNotifications();
          await getUnreadCount();
        }
      )
      .subscribe();

    setRealtimeSubscription(channel);

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user?.id, fetchNotifications, getUnreadCount]);

  /**
   * Initial data load
   */
  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
      getUnreadCount();
      getPreferences();
    }
  }, [user?.id, fetchNotifications, getUnreadCount, getPreferences]);

  /**
   * Memoized selectors for filtered notifications
   */
  const getNotificationsByType = useCallback((type) => {
    return notifications.filter(n => n.type === type);
  }, [notifications]);

  const getUnreadNotifications = useCallback(() => {
    return notifications.filter(n => !n.is_read);
  }, [notifications]);

  const getHighPriorityNotifications = useCallback(() => {
    return notifications.filter(n => n.data?.priority === 'high' || n.data?.priority === 'urgent');
  }, [notifications]);

  /**
   * Context value
   */
  const value = useMemo(() => ({
    // State
    notifications,
    unreadCount,
    preferences,
    isLoading,
    error,
    
    // Actions
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    getUnreadCount,
    getPreferences,
    updatePreferences,
    
    // Selectors
    getNotificationsByType,
    getUnreadNotifications,
    getHighPriorityNotifications
  }), [
    notifications,
    unreadCount,
    preferences,
    isLoading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    getUnreadCount,
    getPreferences,
    updatePreferences,
    getNotificationsByType,
    getUnreadNotifications,
    getHighPriorityNotifications
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
